import { useEffect, useState } from "react";
import SeoHead from "./SeoHead";
import { fetchShareMeta } from "../lib/api";

/**
 * Fetches /api/share/meta/{historyId} and stamps OG / Twitter card meta tags
 * on the host page. Renders nothing visible.
 *
 * Adds the additional spec tags (og:image:width/height, twitter:card,
 * twitter:title, twitter:image) on top of the base set provided by SeoHead.
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function ResultOgMeta({ historyId }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!historyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchShareMeta(historyId);
        if (!cancelled) setMeta(m);
      } catch {
        // Silent failure — page just won't get bespoke OG tags. Default site
        // OG remains in place.
      }
    })();
    return () => { cancelled = true; };
  }, [historyId]);

  // Apply image / twitter-specific tags whenever meta is available.
  useEffect(() => {
    if (!meta) return undefined;
    const origin = window.location.origin;
    const absoluteImage = meta.image_url?.startsWith("http")
      ? meta.image_url
      : origin + meta.image_url;

    const created = [
      upsertMeta('meta[property="og:image"]', {
        property: "og:image", content: absoluteImage,
      }),
      upsertMeta('meta[property="og:image:width"]', {
        property: "og:image:width", content: "1200",
      }),
      upsertMeta('meta[property="og:image:height"]', {
        property: "og:image:height", content: "630",
      }),
      upsertMeta('meta[name="twitter:card"]', {
        name: "twitter:card", content: "summary_large_image",
      }),
      upsertMeta('meta[name="twitter:title"]', {
        name: "twitter:title", content: meta.title,
      }),
      upsertMeta('meta[name="twitter:image"]', {
        name: "twitter:image", content: absoluteImage,
      }),
    ];

    return () => {
      created.forEach((node) => {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    };
  }, [meta]);

  if (!meta) return null;
  return (
    <SeoHead
      title={meta.title}
      description={meta.description}
      canonicalPath={meta.card_url}
    />
  );
}
